import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Wordmark } from "@/components/landing/Wordmark";
import SiteFooter from "@/components/landing/SiteFooter";

/**
 * SPA-level 404. Rendered by the catch-all route in App.js for every path
 * that does not have an explicit route (i.e. everything except `/`,
 * `/privacy`, `/terms`).
 *
 * Note on HTTP status: this is a client-side 404. The edge (Emergent's
 * managed Cloudflare layer) still returns HTTP 200 with the SPA index for
 * every path, because there is no server-side route table. Users and
 * crawlers see this NotFound page rendered in the DOM, and the page emits
 * a `<meta name="robots" content="noindex">` at runtime so search engines
 * do not index broken paths — but the wire-level status remains 200 until
 * the edge is configured to distinguish valid SPA routes. Flagged for the
 * app / platform team to address if a true HTTP 404 is required for
 * compliance signalling.
 */
export const NotFoundPage = () => {
  const location = useLocation();

  useEffect(() => {
    // Set a per-page robots meta so misfired crawlers stop indexing garbage
    // paths. This is a runtime mutation — the base document meta remains
    // `noindex, follow` today (pre-launch), so this is currently redundant,
    // but it survives the eventual noindex flip.
    const prevTitle = document.title;
    document.title = "Not found — Voxium Script";

    let meta = document.querySelector('meta[name="robots"][data-notfound]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      meta.setAttribute("content", "noindex, nofollow");
      meta.setAttribute("data-notfound", "true");
      document.head.appendChild(meta);
    }

    return () => {
      document.title = prevTitle;
      const m = document.querySelector('meta[name="robots"][data-notfound]');
      if (m) m.remove();
    };
  }, []);

  return (
    <main
      className="min-h-screen bg-cream flex flex-col"
      data-testid="notfound-page"
    >
      {/* Header — wordmark home link, no signin/CTA on error pages */}
      <header className="w-full px-6 sm:px-10 lg:px-16 pt-8 pb-4 border-b border-cream-200">
        <div className="max-w-[78rem] mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center tap-extend"
            aria-label="Voxium Script home"
            data-testid="notfound-home-link"
          >
            <Wordmark tone="light" size="nav" testId="notfound-wordmark" />
          </Link>
        </div>
      </header>

      <section className="flex-1 w-full px-6 sm:px-10 lg:px-16 py-16 sm:py-24 flex items-center">
        <div className="max-w-prose3 mx-auto w-full">
          <p
            className="eyebrow text-gold-900 mb-6"
            data-testid="notfound-eyebrow"
          >
            Off the page
          </p>
          <h1
            className="display-serif text-nearblack text-5xl sm:text-6xl leading-[1.02] mb-8"
            data-testid="notfound-title"
          >
            No scene here.
          </h1>
          <p
            className="font-body text-nearblack/80 text-lg leading-relaxed mb-10 max-w-[52ch]"
            data-testid="notfound-body"
          >
            The path{" "}
            <code
              className="font-ui text-base bg-cream-200/50 px-1.5 py-0.5 rounded-sm"
              data-testid="notfound-path"
            >
              {location.pathname}
            </code>{" "}
            isn&apos;t part of Voxium Script. Head back to the homepage or take
            one of the doors below.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-16">
            <Link
              to="/"
              className="btn-gold"
              data-testid="notfound-cta-home"
            >
              Back to homepage
            </Link>
            <Link
              to="/privacy"
              className="btn-text tap-extend text-sm"
              data-testid="notfound-cta-privacy"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="btn-text tap-extend text-sm"
              data-testid="notfound-cta-terms"
            >
              Terms of Service
            </Link>
            <Link
              to="/refunds"
              className="btn-text tap-extend text-sm"
              data-testid="notfound-cta-refunds"
            >
              Refunds
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default NotFoundPage;
